import { useQuery } from '@tanstack/react-query'
import { useOutletContext } from 'react-router-dom'
import { api } from '../lib/api'
import type { useLiveFeed } from '../lib/live'
import { bytes, count, dateTime } from '../lib/format'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { Bar, Chip, ErrorState, Loading, Metric, Panel } from '../components/ui'

interface Health {
  node: string
  uptime: string
  tenants: { total: number; resellers: number; clients: number }
  fleet: { users: number; devices: number; devicesOnline: number; vehicles: number }
  throughput: { positionsLastHour: number; positionsLastMinute: number; packetsPerSecond: number }
  queues: {
    unacknowledgedEvents: number
    openTrips: number
    pendingAlerts: number
    failedAlerts24h: number
    queuedCommands: number
  }
  storage: { hypertables?: { table: string; size: string; bytes: number; chunks: number }[] }
  backgroundJobs: {
    id: number
    name: string
    interval: string
    lastStatus: string
    lastSuccess?: string
    failures: number
  }[]
  database?: { size: string; connections: number; maxConnections: number }
  pool?: { acquired: number; idle: number; total: number; maxConns: number }
  websocket?: { clients: number; published: number; dropped: number }
  ingest?: Record<string, number>
}

/**
 * Tier 1 system health (FSD 3.1): socket servers, queue backlogs, API
 * throughput and time-series storage.
 */
export function HealthPage() {
  const feed = useOutletContext<ReturnType<typeof useLiveFeed>>()

  const health = useQuery({
    queryKey: ['platform-health'],
    queryFn: () => api<Health>('/platform/health'),
    refetchInterval: 10_000,
  })

  if (health.isLoading) return <Loading label="Reading system health" />
  if (health.error) return <ErrorState error={health.error} retry={() => void health.refetch()} />

  const h = health.data!
  const poolUse = h.pool ? (h.pool.acquired / Math.max(h.pool.maxConns, 1)) * 100 : 0
  const connUse = h.database
    ? (h.database.connections / Math.max(h.database.maxConnections, 1)) * 100
    : 0
  const totalBytes = h.storage.hypertables?.reduce((s, t) => s + t.bytes, 0) ?? 0

  return (
    <Page
      title="System health"
      subtitle={`Node ${h.node} · up ${h.uptime} · refreshing every 10 s`}
    >
      <div className="space-y-4">
        {/* --- throughput --- */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger">
          <Metric
            label="Ingest rate"
            value={h.throughput.packetsPerSecond.toFixed(1)}
            unit="packets/s"
            tone="accent"
            hint={`${count(h.throughput.positionsLastHour)} in the last hour`}
            icon={<Icon name="pulse" size={13} />}
          />
          <Metric
            label="Devices online"
            value={count(h.fleet.devicesOnline)}
            hint={`of ${count(h.fleet.devices)} registered`}
            tone={
              h.fleet.devices > 0 && h.fleet.devicesOnline / h.fleet.devices < 0.8 ? 'idle' : 'moving'
            }
            icon={<Icon name="signal" size={13} />}
          />
          <Metric
            label="WebSocket clients"
            value={count(h.websocket?.clients ?? 0)}
            hint={
              h.websocket?.dropped
                ? `${count(h.websocket.dropped)} frames dropped`
                : 'no frames dropped'
            }
            tone={h.websocket?.dropped ? 'idle' : 'default'}
            icon={<Icon name="users" size={13} />}
          />
          <Metric
            label="Time-series data"
            value={bytes(totalBytes)}
            hint={`${h.storage.hypertables?.length ?? 0} hypertables`}
            icon={<Icon name="chip" size={13} />}
          />
        </section>

        <div className="grid lg:grid-cols-2 gap-4 items-start">
          {/* --- queues --- */}
          <Panel title="Queues and backlogs" subtitle="A number that only grows is the warning sign">
            <div className="space-y-3">
              <QueueRow
                label="Pending alert deliveries"
                value={h.queues.pendingAlerts}
                warnAbove={500}
                hint="Email, SMS and webhook notifications waiting to be sent"
              />
              <QueueRow
                label="Failed deliveries (24 h)"
                value={h.queues.failedAlerts24h}
                warnAbove={1}
                hint="Usually an SMTP credential or an unreachable webhook"
              />
              <QueueRow
                label="Queued device commands"
                value={h.queues.queuedCommands}
                warnAbove={100}
                hint="Waiting for each unit's next connection"
              />
              <QueueRow
                label="Open trips"
                value={h.queues.openTrips}
                warnAbove={h.fleet.vehicles}
                hint="Journeys in progress; more than the vehicle count means trips are not closing"
              />
              <QueueRow
                label="Unacknowledged events (24 h)"
                value={h.queues.unacknowledgedEvents}
                warnAbove={1000}
                hint="Not an error — but a large number means nobody is reading the alerts"
              />
            </div>
          </Panel>

          {/* --- connections --- */}
          <Panel title="Database">
            <div className="space-y-4">
              {h.database && (
                <>
                  <Gauge
                    label="Server connections"
                    value={h.database.connections}
                    max={h.database.maxConnections}
                    pct={connUse}
                  />
                  <div className="flex justify-between text-[12px]">
                    <span className="label">Database size</span>
                    <span className="tnum text-[var(--color-ink)]">{h.database.size}</span>
                  </div>
                </>
              )}
              {h.pool && (
                <Gauge
                  label="API pool"
                  value={h.pool.acquired}
                  max={h.pool.maxConns}
                  pct={poolUse}
                  hint={`${h.pool.idle} idle`}
                />
              )}

              <div className="rule-t pt-3">
                <div className="label mb-2">Local feed</div>
                <div className="flex items-center gap-2.5">
                  <span
                    className={`dot ${feed.status === 'live' ? 'dot-live' : ''}`}
                    style={{
                      background: feed.status === 'live' ? 'var(--color-moving)' : 'var(--color-idle)',
                      color: feed.status === 'live' ? 'var(--text-moving)' : 'var(--text-idle)',
                    }}
                  />
                  <span className="text-[13px]">{feed.status}</span>
                  <span className="grow" />
                  <span className="tnum text-[12px] text-[var(--color-ink-3)]">
                    {count(feed.packets)} frames this session
                  </span>
                </div>
              </div>
            </div>
          </Panel>

          {/* --- storage --- */}
          <Panel title="Time-series storage" padded={false}>
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Hypertable</th>
                  <th style={{ textAlign: 'right' }}>Chunks</th>
                  <th style={{ textAlign: 'right' }}>Size</th>
                  <th style={{ width: '32%' }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {h.storage.hypertables?.length ? (
                  h.storage.hypertables.map((t) => (
                    <tr key={t.table}>
                      <td className="text-[var(--color-ink)]">{t.table}</td>
                      <td className="tnum text-right">{count(t.chunks)}</td>
                      <td className="tnum text-right">{t.size}</td>
                      <td>
                        <Bar value={t.bytes} max={totalBytes || 1} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6 text-[var(--color-ink-4)]">
                      No hypertable information available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel>

          {/* --- background jobs --- */}
          <Panel
            title="TimescaleDB jobs"
            subtitle="Compression, retention and continuous aggregate refreshes"
            padded={false}
          >
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Every</th>
                  <th>Last run</th>
                  <th style={{ textAlign: 'right' }}>Failures</th>
                </tr>
              </thead>
              <tbody>
                {h.backgroundJobs?.length ? (
                  h.backgroundJobs.map((j) => (
                    <tr key={j.id}>
                      <td className="text-[var(--color-ink)]">{j.name}</td>
                      <td className="tnum text-[11px]">{j.interval}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Chip
                            tone={
                              j.lastStatus === 'Success' ? 'ok' : j.lastStatus === 'never run' ? 'muted' : 'critical'
                            }
                          >
                            {j.lastStatus}
                          </Chip>
                          {j.lastSuccess && (
                            <span className="tnum text-[10px] text-[var(--color-ink-4)]">
                              {dateTime(j.lastSuccess)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="tnum text-right"
                        style={{ color: j.failures > 0 ? 'var(--text-critical)' : undefined }}
                      >
                        {count(j.failures)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6 text-[var(--color-ink-4)]">
                      No jobs registered
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel>
        </div>

        {/* --- ingest node --- */}
        {h.ingest && (
          <Panel title="Ingestion pipeline" subtitle="Counters since this process started">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(h.ingest).map(([k, v]) => (
                <div key={k}>
                  <div className="label text-[10px] mb-1">{humanise(k)}</div>
                  <div
                    className="tnum text-[15px]"
                    style={{
                      color:
                        (k === 'dropped' || k === 'rejected') && Number(v) > 0
                          ? 'var(--text-critical)'
                          : 'var(--color-ink)',
                    }}
                  >
                    {typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(1) : count(Number(v))}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {/* --- scale --- */}
        <Panel title="Platform scale">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Figure label="Accounts" value={h.tenants.total} />
            <Figure label="Resellers" value={h.tenants.resellers} />
            <Figure label="Clients" value={h.tenants.clients} />
            <Figure label="Vehicles" value={h.fleet.vehicles} />
            <Figure label="Users" value={h.fleet.users} />
          </div>
        </Panel>
      </div>
    </Page>
  )
}

function QueueRow({
  label,
  value,
  warnAbove,
  hint,
}: {
  label: string
  value: number
  warnAbove: number
  hint: string
}) {
  const bad = value >= warnAbove
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-[13px] text-[var(--color-ink-2)]">{label}</div>
        <div className="text-[11px] text-[var(--color-ink-4)] leading-snug">{hint}</div>
      </div>
      <span
        className="tnum text-[15px] shrink-0"
        style={{ color: bad ? 'var(--text-warn)' : 'var(--color-ink)' }}
      >
        {count(value)}
      </span>
    </div>
  )
}

function Gauge({
  label,
  value,
  max,
  pct,
  hint,
}: {
  label: string
  value: number
  max: number
  pct: number
  hint?: string
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline">
        <span className="label">{label}</span>
        <span className="tnum text-[12px] text-[var(--color-ink-2)]">
          {count(value)} / {count(max)}
          {hint && <span className="text-[var(--color-ink-4)]"> · {hint}</span>}
        </span>
      </div>
      <Bar value={pct} tone={pct > 85 ? 'critical' : pct > 65 ? 'warn' : 'ok'} />
    </div>
  )
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="label text-[10px] mb-1">{label}</div>
      <div className="tnum text-[18px] font-semibold tracking-tight">{count(value)}</div>
    </div>
  )
}

function humanise(key: string) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())
}
